/**
 * 数据集字段别名加载器：从固定目录（bindings.aliasDir）读取字段别名知识库。
 *
 * 用途：解决"范例报表 label 与模板标签体系不一致导致 suggest 零命中"的问题。
 * 用户把"标签/别名 → 数据集字段"的映射按数据集拆分成独立 JSON 文件放入固定目录，
 * suggest 时作为**实时补充源**并入知识库，从而让模板英文标签（如 DATE/CUSTOMER ID）
 * 能直接命中并锚定到正确字段。
 *
 * 别名文件格式（一个数据集一个文件，如 账单.json）：
 * ```json
 * {
 *   "dataset": "账单",
 *   "aliases": [
 *     { "field": "stmdate", "label": "DATE", "aliases": ["开单日期", "账单日期"] },
 *     { "field": "jobno",   "label": "SHIPMENT", "aliases": ["订舱号", "工作单号"] }
 *   ]
 * }
 * ```
 * - field：数据集真实字段名（须与 BeanDataset / --fields 一致）
 * - label：主标签（模板上通常出现的文本）
 * - aliases：可选，同一字段的其他叫法
 *
 * 生成的知识库：所有 label（含 aliases）都指向对应 field，置信度 1.0，
 * 并标记 source: 'alias'。命中时由 BindingDictLoader.query 锚定胜出。
 */

import type { BindingDict, BindingDictEntry } from '../core/template/mineFromIr.js';
import { getLogger } from '../core/logger.js';

const log = getLogger('core:template');

interface AliasItem {
  field?: string;
  label?: string;
  aliases?: string[];
}

interface AliasFile {
  dataset?: string;
  aliases?: AliasItem[];
}

/** 空知识库（目录为空或不存在时）。 */
function emptyDict(): BindingDict {
  return {
    labels: {},
    positions: {},
    patterns: {},
    version: 0,
    miningDate: '',
    exampleCount: 0,
    bindingCount: 0,
  };
}

/** 构造一条 label → field 的强映射条目。 */
function buildEntry(field: string): BindingDictEntry {
  return { fields: [field], confidence: [1.0], count: 1, sources: ['alias'] };
}

/**
 * 从固定目录加载数据集字段别名知识库。
 *
 * @param aliasDir 固定别名目录（扫描 *.json，忽略下划线开头文件）
 * @returns 以 labels 层为主的 BindingDict（positions / patterns 为空）
 */
export async function loadFieldAliases(aliasDir: string): Promise<BindingDict> {
  const dict = emptyDict();
  let fileCount = 0;
  let aliasCount = 0;

  try {
    const fs = await import('node:fs/promises');
    const entries = await fs.readdir(aliasDir, { withFileTypes: true });
    for (const entry of entries) {
      if (!entry.isFile()) continue;
      if (!entry.name.endsWith('.json')) continue;
      if (entry.name.startsWith('_')) continue; // 下划线开头 = 非激活文件
      fileCount++;
      const fullPath = `${aliasDir}/${entry.name}`;
      try {
        const content = await fs.readFile(fullPath, 'utf-8');
        const parsed = JSON.parse(content) as AliasFile;
        for (const item of parsed.aliases ?? []) {
          const field = item.field?.trim();
          if (!field) continue;
          const labels = new Set<string>();
          if (item.label?.trim()) labels.add(item.label.trim());
          for (const a of item.aliases ?? []) {
            if (a?.trim()) labels.add(a.trim());
          }
          for (const label of labels) {
            const entry = dict.labels[label];
            if (entry) {
              if (!entry.fields.includes(field)) {
                entry.fields.push(field);
                entry.confidence.push(1.0);
                entry.count++;
              }
            } else {
              dict.labels[label] = buildEntry(field);
            }
            aliasCount++;
          }
        }
      } catch (error) {
        log.warn(`⚠️ 字段别名文件解析失败 (${fullPath}): ${(error as Error).message}`);
      }
    }
  } catch (error) {
    log.warn(`⚠️ 字段别名目录不可用 (${aliasDir}): ${(error as Error).message}`);
    return emptyDict();
  }

  if (fileCount > 0 && aliasCount > 0) {
    dict.version = 1;
    dict.miningDate = new Date().toISOString();
    dict.exampleCount = fileCount;
    dict.bindingCount = aliasCount;
  }
  logLoaded(dict, fileCount);
  return dict;
}

/** 打印别名知识库加载日志。 */
function logLoaded(dict: BindingDict, fileCount: number): void {
  const labelCount = Object.keys(dict.labels).length;
  if (labelCount === 0) {
    log.info(`📖 字段别名知识库已加载 (labels=0, 无可用别名文件或别名)`);
    return;
  }
  log.info(
    `📖 字段别名知识库已加载 (files=${fileCount}, labels=${labelCount}, bindings=${dict.bindingCount})`,
  );
}
